/**
 * src/lib/api/notifications.js — In-app notifications API (TICKET-030).
 *
 * Notifications are written by a SECURITY DEFINER Postgres trigger
 * (notify_task_assignment) on tasks INSERT or UPDATE OF assignee_id.
 * The frontend reads its own rows (RLS: user_id = auth.uid()) and updates
 * read_at to dismiss.
 *
 * Realtime: subscribe via supabase.channel('notifications:<userId>:<suffix>')
 * with postgres_changes filter user_id=eq.<userId>. Random suffix per mount
 * avoids the channel-collision class fixed in TICKET-025.
 */

import { supabase } from '../supabase';

export async function fetchNotifications({ unreadOnly = false, limit = 50 } = {}) {
  try {
    let query = supabase
      .from('notifications')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);

    if (unreadOnly) query = query.is('read_at', null);

    const { data, error } = await query;
    if (error) {
      console.warn('[notifications] fetch error:', error.message);
      return [];
    }
    return data || [];
  } catch (err) {
    console.warn('[notifications] fetchNotifications failed:', err.message);
    return [];
  }
}

export async function markNotificationRead(notificationId) {
  if (!notificationId) return { success: false, error: 'notificationId is required' };
  try {
    const { error } = await supabase
      .from('notifications')
      .update({ read_at: new Date().toISOString() })
      .eq('id', notificationId)
      .is('read_at', null);
    if (error) return { success: false, error: error.message };
    return { success: true };
  } catch (err) {
    return { success: false, error: err.message };
  }
}

export async function markAllRead() {
  try {
    const { data: session } = await supabase.auth.getSession();
    const userId = session?.session?.user?.id;
    if (!userId) return { success: false, error: 'no auth session' };

    const { error } = await supabase
      .from('notifications')
      .update({ read_at: new Date().toISOString() })
      .eq('user_id', userId)
      .is('read_at', null);
    if (error) return { success: false, error: error.message };
    return { success: true };
  } catch (err) {
    return { success: false, error: err.message };
  }
}

export async function markTaskNotificationsRead(taskId) {
  if (!taskId) return { success: false, error: 'taskId is required' };
  try {
    const { data: session } = await supabase.auth.getSession();
    const userId = session?.session?.user?.id;
    if (!userId) return { success: false, error: 'no auth session' };

    const { error } = await supabase
      .from('notifications')
      .update({ read_at: new Date().toISOString() })
      .eq('user_id', userId)
      .eq('task_id', taskId)
      .is('read_at', null);
    if (error) return { success: false, error: error.message };
    return { success: true };
  } catch (err) {
    return { success: false, error: err.message };
  }
}

export function subscribeToNotifications(userId, onInsert) {
  if (!userId) return null;
  const suffix = Math.random().toString(36).slice(2, 10);
  const channel = supabase
    .channel(`notifications:${userId}:${suffix}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${userId}`,
      },
      (payload) => {
        if (payload?.new) onInsert(payload.new);
      },
    )
    .subscribe();
  return channel;
}
